import { memo, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn, IconButton, WeightGlyph } from '@/components/ui';
import { WEIGHT_OPTIONS } from '@/logic/weights';
import { CHROME } from './MapChrome';

const Swatch = ({ children }: { children: ReactNode }) => (
  <svg
    width="16"
    height="16"
    viewBox="-8 -8 16 16"
    aria-hidden="true"
    className="shrink-0 overflow-visible"
  >
    {children}
  </svg>
);

const Item = ({
  swatch,
  children,
}: {
  swatch: ReactNode;
  children: ReactNode;
}) => (
  <li className="flex min-w-0 items-center gap-2">
    {swatch}
    <span className="truncate">{children}</span>
  </li>
);

const Section = ({
  title,
  columns = false,
  children,
}: {
  title: string;
  columns?: boolean;
  children: ReactNode;
}) => (
  <div className="space-y-1.5">
    <p className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
      {title}
    </p>
    <ul
      className={cn(columns ? 'grid grid-cols-2 gap-x-3 gap-y-1' : 'space-y-1')}
    >
      {children}
    </ul>
  </div>
);

export interface MapLegendProps {
  id: string;
  open: boolean;
  onClose: () => void;
  /** 'fit' overlays transformed Record on Field; 'source' shows both datasets. */
  mode: 'fit' | 'source';
  hasFit: boolean;
  residualsShown: boolean;
  compareLabel: string | null;
  uncertaintyShown: boolean;
  hasUnmatched: boolean;
  hasFlagged: boolean;
  className?: string;
}

/** Small floating key to the map's symbols; only lists what can appear. */
export const MapLegend = memo(function MapLegend({
  id,
  open,
  onClose,
  mode,
  hasFit,
  residualsShown,
  compareLabel,
  uncertaintyShown,
  hasUnmatched,
  hasFlagged,
  className,
}: MapLegendProps) {
  const fitMode = mode === 'fit';
  return (
    <div
      id={id}
      role="region"
      aria-label="Map legend"
      hidden={!open}
      className={cn(
        CHROME,
        'w-56 max-w-[calc(100%-1rem)] animate-fade-in bg-surface-raised/95 p-3 text-xs text-fg shadow-md',
        className
      )}
    >
      <div className="-mr-1.5 -mt-1.5 mb-1 flex items-center justify-between">
        <p className="text-sm font-semibold">Legend</p>
        <IconButton
          aria-label="Close legend"
          tooltip={false}
          size="sm"
          className="size-7"
          icon={<X />}
          onClick={onClose}
        />
      </div>
      <div className="space-y-3">
        <Section title="Points">
          {fitMode ? (
            <>
              <Item
                swatch={
                  <Swatch>
                    <circle r={4.5} className="fill-field" />
                  </Swatch>
                }
              >
                Field (observed)
              </Item>
              {hasFit && (
                <Item
                  swatch={
                    <Swatch>
                      <circle r={3.25} className="fill-record" />
                    </Swatch>
                  }
                >
                  Record (transformed)
                </Item>
              )}
            </>
          ) : (
            <>
              <Item
                swatch={
                  <Swatch>
                    <circle r={4.5} className="fill-record" />
                  </Swatch>
                }
              >
                Record
              </Item>
              <Item
                swatch={
                  <Swatch>
                    <circle r={4.5} className="fill-field" />
                  </Swatch>
                }
              >
                Field
              </Item>
            </>
          )}
          {hasUnmatched && (
            <Item
              swatch={
                <Swatch>
                  <rect
                    x={-3.5}
                    y={-3.5}
                    width={7}
                    height={7}
                    rx={1}
                    className="fill-none stroke-fg-muted"
                    strokeWidth={1.25}
                  />
                </Swatch>
              }
            >
              Not paired
            </Item>
          )}
        </Section>

        <Section title="Weight" columns>
          {WEIGHT_OPTIONS.map((option) => (
            <Item
              key={option.status}
              swatch={
                <span className="flex size-4 items-center justify-center">
                  <WeightGlyph status={option.status} />
                </span>
              }
            >
              {option.label}
            </Item>
          ))}
        </Section>

        <Section title="Marks">
          <Item
            swatch={
              <Swatch>
                <circle
                  r={6.5}
                  className="fill-accent/15 stroke-accent"
                  strokeWidth={1.5}
                />
              </Swatch>
            }
          >
            Selected pair
          </Item>
          {hasFlagged && (
            <Item
              swatch={
                <Swatch>
                  <circle
                    r={5.5}
                    className="fill-none stroke-danger"
                    strokeWidth={2}
                  />
                </Swatch>
              }
            >
              Flagged for review
            </Item>
          )}
        </Section>

        {hasFit && (residualsShown || compareLabel || uncertaintyShown) && (
          <Section title="Fit">
            {residualsShown && (
              <Item
                swatch={
                  <Swatch>
                    <path
                      d="M-6.5 4.5 3 -5"
                      className="stroke-residual"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                    />
                    <path
                      d="M5.5 -7.5 0.2 -5.9 4 -2Z"
                      className="fill-residual"
                    />
                  </Swatch>
                }
              >
                Residual vector
              </Item>
            )}
            {compareLabel && (
              <>
                <Item
                  swatch={
                    <Swatch>
                      <circle
                        r={4}
                        className="fill-none stroke-fg-muted"
                        strokeWidth={1.25}
                        strokeDasharray="2 1.75"
                      />
                    </Swatch>
                  }
                >
                  {compareLabel} fit position
                </Item>
                <Item
                  swatch={
                    <Swatch>
                      <path
                        d="M-7 0H7"
                        className="stroke-record"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                      />
                    </Swatch>
                  }
                >
                  Fit movement (true scale)
                </Item>
              </>
            )}
            {uncertaintyShown && (
              <Item
                swatch={
                  <Swatch>
                    <circle
                      r={6}
                      className="fill-record/5 stroke-record/60"
                      strokeWidth={1}
                      strokeDasharray="2.5 2"
                    />
                  </Swatch>
                }
              >
                95% uncertainty circle
              </Item>
            )}
          </Section>
        )}
      </div>
    </div>
  );
});
